/**
 * The one rendering of an AI recovery-triage job, shared by every pane
 * that can start one.
 *
 * Extracted from `DiagnosePane` unchanged when the Tickets tab gained
 * its own "Analyze with AI": the honest empty/failure copy here is the
 * product of several rounds of "the model said nothing and the UI
 * blamed the catalogue", and a second hand-written copy of it in
 * TicketsPane would have drifted out of agreement with this one the
 * first time either was corrected. The failure copy, the deadline
 * reading, the elapsed formatter and the `Run →` control all live here
 * for the same reason — they are the parts a second caller needs, and
 * the parts that must not fork.
 *
 * What is NOT here is where `Run →` points: an issue and a ticket
 * reach ActPane by different routes (and a ticket may not be able to
 * reach it at all), so the caller supplies `runHref` and this renders
 * the link only when it yields one.
 *
 * The live-run rendering (the progress list, the streaming assessment,
 * the age + Re-analyze pair) landed here for the same non-forking
 * reason. Its behavioural half -- starting a job, the poll cadence,
 * the cache-hit rule -- is `./use-analysis-job`, split out only
 * because a file exporting components may export nothing else.
 */
import { Link } from 'react-router-dom';
import type {
  AnalysisFailureReason,
  SupportAnalysisResponse,
  SupportAnalysisResult,
} from '../api/supportClient';
import { analysisIsRunning } from './use-analysis-job';

/**
 * Honest copy for a `done` analysis with zero candidates. An empty
 * list used to render one ambiguous line whether the model genuinely
 * matched nothing or the run failed in one of three silent ways — most
 * notably a model without tool-calling support (the production
 * `google/gemma-4-31b-it` pin), which produces `unparseable_output`
 * every single time.
 *
 * A value is a fixed string, or a function of the result for the one
 * reason whose honest sentence depends on a measurement the result
 * carries (`deadline_exceeded`, below). The strings are unchanged.
 */
type EmptyCandidatesCopy = string | ((result: SupportAnalysisResult) => string);

const EMPTY_CANDIDATES_COPY: Record<AnalysisFailureReason, EmptyCandidatesCopy> = {
  unparseable_output:
    'The model did not return a parseable answer — a model without tool-calling support produces exactly this (check the admin Settings model).',
  no_final_answer: 'The model returned no final answer.',
  unknown_runbooks_only:
    'The model proposed runbooks not in the catalogue; they were dropped rather than shown.',
  deadline_exceeded: deadlineNoAnswerCopy,
  // Deliberately distinct from `deadline_exceeded`: that one means OUR
  // wall clock ran out, this one means the provider went quiet while we
  // still had budget. They point at different knobs, and conflating
  // them sends the next investigator to the wrong one.
  llm_request_timeout:
    'The model provider did not respond in time — the run stopped waiting, rather than running out of its overall time.',
};

/** Where to send an engineer whose run ran out of time. Both knobs
 *  that shorten a model step -- the model itself and, since
 *  2026-09-03, the "Model thinking" switch beside it (off | on, off
 *  by default) -- are platform settings in the admin console, so the
 *  two deadline texts below point at the same place with the same
 *  words: this is the one sentence they share, appended whole.
 *
 *  Conditional ("if ... is on") rather than the flat "turning Model
 *  thinking off" of the same-day draft, because the switch is off by
 *  default and the result does not carry its state: a flat
 *  instruction sends most engineers who read it to a switch already
 *  in the recommended position, and from there nowhere. So the
 *  sentence names the next move in each state -- thinking on: turn it
 *  off; already off: the model is the knob -- and promises no outcome.
 *  The draft's "would let it finish" was measured on exactly one
 *  model and is false for another: deepseek-r1 thinks for 33 s per
 *  step whatever the request says (2026-09-03 probe), so the only fix
 *  there is the second half of the sentence.
 *
 *  The switch is named exactly as the admin console labels it, because
 *  this sentence is the only pointer the engineer gets. An earlier
 *  draft the same day said "a lower reasoning effort" -- the name of a
 *  four-level knob (low | medium | high | off) Akash replaced with the
 *  plain switch before the day was out, on the grounds that the
 *  setting must mean what it says. What "off" buys is measured, not
 *  hoped for: on z-ai/glm-5.3-flash -- the model an admin switched to
 *  on 2026-09-02 (12:53 UTC), and the model behind the 24.1 s /
 *  38.6 s / 104 s steps measured on 2026-09-03 that ended every
 *  analysis in `deadline_exceeded` -- thinking off is 3.2 s and 0
 *  reasoning tokens (6.6 s with tools attached) against 22.6 s with it
 *  on.
 *
 *  Says "a faster model", never WHICH model ran: this panel does not
 *  name the model (see `AnalysisResultView`), and the advice does not
 *  need the name. */
const DEADLINE_ADVICE =
  "If Model thinking is on in the admin console's Settings, turn it off; otherwise choose a faster model there.";

/** `llm_ms_max` as a number worth showing, or null. An older
 *  api-server omits the field, and the server sends 0 when no model
 *  call completed at all -- in both cases there is nothing to measure,
 *  so the sentences below say nothing about it rather than "took 0s". */
function slowestStepMs(result: SupportAnalysisResult): number | null {
  const ms = result.llm_ms_max;
  return typeof ms === 'number' && ms > 0 ? ms : null;
}

/** The copy for a run the deadline stopped before any answer came.
 *
 *  A function of the result rather than a fixed string because the
 *  fixed string sent the engineer nowhere. On 2026-09-03 production
 *  ran a 90 s triage whose model steps took 24 s and 39 s (an admin
 *  had switched, on 2026-09-02, to a reasoning model that thinks by
 *  default, and there was no Model thinking switch yet to stop it),
 *  then spent its last
 *  27 s on a forced final-answer turn that produced no text at all;
 *  this panel showed only "hit its time limit before the model reached
 *  an answer — nothing was matched because it never finished", which
 *  is true and useless: it names no cause and no knob. The slowest
 *  single model call is the number that separates
 *  "one slow model" from "too many rounds", and both fixes for the
 *  former are admin-console settings, so the sentence now carries the
 *  measurement and the pointer. Without the measurement (older
 *  server, or no call completed) it keeps the pointer and drops the
 *  number rather than inventing one. */
function deadlineNoAnswerCopy(result: SupportAnalysisResult): string {
  const slowest = slowestStepMs(result);
  const lead = 'The run hit its time limit before the model reached an answer.';
  if (slowest != null) {
    return `${lead} Its slowest model step took ${formatElapsed(slowest)}. ${DEADLINE_ADVICE}`;
  }
  return `${lead} ${DEADLINE_ADVICE}`;
}

/** `unparseable_output` next to a parsed `assessment` means the answer
 *  was read far enough to yield a diagnosis and only its runbook list
 *  was malformed. The non-tool-calling hint above would send the
 *  engineer to the admin Settings model for nothing. */
const MALFORMED_CANDIDATES_COPY =
  "The answer's runbook list was malformed, so no candidates could be shown — the assessment above still stands.";

/** Module-private, like the two helpers below: nothing outside this
 *  file should be re-deriving any of this — the point of the
 *  extraction was that there is exactly one answer to "what does an
 *  empty candidate list mean", "was this run cut short" and "how long
 *  did it take", and it is rendered here. */
function emptyCandidatesCopy(result: SupportAnalysisResult, hasAssessment: boolean): string {
  const reason = result.failure_reason;
  if (reason === 'unparseable_output' && hasAssessment) return MALFORMED_CANDIDATES_COPY;
  // A reason this build has never heard of (a newer server) reads as
  // undefined here and falls through to the neutral line, exactly as
  // it did when every value was a string.
  const copy: EmptyCandidatesCopy | undefined = reason ? EMPTY_CANDIDATES_COPY[reason] : undefined;
  if (copy === undefined) return 'The model examined the evidence and matched no catalogued runbook.';
  return typeof copy === 'function' ? copy(result) : copy;
}

/** The deadline signal is read from BOTH shapes the api-server may
 *  send it in -- a dedicated boolean, or `failure_reason` carrying the
 *  same fact -- because a run that was cut short is the one case where
 *  a thin answer is not the model's fault, and an older server that
 *  reports neither must read as "not cut short" rather than crash. */
function analysisDeadlineExceeded(result: SupportAnalysisResult): boolean {
  return result.deadline_exceeded === true || result.failure_reason === 'deadline_exceeded';
}

/** "took 3.2s" / "took 47s". Sub-10s runs keep a decimal because the
 *  difference between 1.2s and 9.8s is the difference between a cached
 *  answer and a real tool loop; past that the decimal is noise. */
function formatElapsed(ms: number): string {
  return ms < 10_000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms / 1000)}s`;
}

/** The offset of one progress step from the start of its run. Unlike
 *  `formatElapsed` this keeps the decimal at every magnitude: the whole
 *  point of the list is which STEP is slow, and "12s, 12s, 47s" hides
 *  the ordering that "12.1s, 12.9s, 47.3s" shows. */
function formatOffset(ms: number): string {
  return `${(Math.max(0, ms) / 1000).toFixed(1)}s`;
}

/** How old a finished result is, in the coarsest unit that has FULLY
 *  elapsed -- floor, the convention every other "2h ago" an engineer
 *  reads all day uses. "2h ago" therefore means "at least two hours
 *  ago": the number shown is never LARGER than the truth, and may be
 *  smaller by up to one unit.
 *
 *  This comment used to claim the opposite (that it rounds so as never
 *  to overstate freshness) while the code floored, which is the one
 *  direction floor cannot deliver. The claim was dropped rather than
 *  the behaviour, deliberately: rounding up buys a literal "never
 *  younger than the truth" at the price of overstating by nearly a
 *  whole unit at every boundary -- a two-hour-old answer reading "3h
 *  ago" -- which is the same lie pointing the other way and reads as a
 *  bug to anyone who checks it against the clock.
 *
 *  What actually keeps a never-expiring server-side cache honest is
 *  not this string. It is the Re-analyze button beside it, and the
 *  exact timestamp carried in the `title` of the very same element for
 *  anyone who needs the real number. This is the coarse cue that sends
 *  them to those two. */
function formatAge(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** The amber callout for a run the deadline stopped AFTER an answer
 *  had arrived. Since 2026-09-03 it also names the slowest model step
 *  when the server measured one, for the same reason as
 *  `deadlineNoAnswerCopy`: "cut short after 3 rounds" says where the
 *  run stopped, "its slowest step took 39s" says why, and only the
 *  second tells the engineer whether re-running will help or whether
 *  the model setting is the thing to change.
 *
 *  The measurement brings `DEADLINE_ADVICE` with it. The first cut
 *  kept the advice in the no-answer copy only, on the grounds that an
 *  answer did arrive here and re-running is the first thing to try --
 *  which is wrong exactly when the number is large: the 38.6 s step
 *  measured on 2026-09-03 exceeded the run's 30 s per-request cap on
 *  its own, so a re-run on the same model with the same switch is cut
 *  short the same way, and "re-run it" was the whole of the advice on
 *  the one case where it cannot help. "Re-run it" still comes first
 *  because it is the cheaper move when the step is 24 s and the
 *  rounds were many; the pointer follows so it is never missing.
 *  Without a measurement there is no evidence that one slow step is
 *  the cause, and the note keeps only "re-run it". */
function deadlineNote(result: SupportAnalysisResult): string {
  const rounds = result.rounds_used;
  const after = rounds != null ? ` after ${rounds} round${rounds === 1 ? '' : 's'}` : '';
  const slowest = slowestStepMs(result);
  const step =
    slowest != null
      ? ` Its slowest model step took ${formatElapsed(slowest)}. ${DEADLINE_ADVICE}`
      : '';
  return `Cut short: the run hit its time limit${after} and returned what it had. Treat this answer as partial — re-run it if it reads thin.${step}`;
}

/** The one control shape for "take me to this runbook, ready to run".
 *  A real `<Link>` rather than a `navigate()` button so it can be
 *  middle-clicked, copied and shared -- the same reason the issue
 *  itself is now deep-linkable. */
export function RunLink({ to, testId, title }: { to: string; testId: string; title: string }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      title={title}
      style={{
        display: 'inline-block',
        fontSize: 12,
        padding: '3px 10px',
        borderRadius: 'var(--nx-r)',
        border: '1px solid var(--nx-border)',
        background: 'var(--nx-bg3)',
        color: 'var(--nx-cyan)',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      Run →
    </Link>
  );
}

export interface AnalysisRunTarget {
  /** Where `Run →` should point for one candidate, or null when this
   *  target cannot reach ActPane at all (a ticket with no site: the
   *  run form has no site to fill in, so a link would land on a form
   *  that cannot be submitted). */
  runHref?: (runbookId: string) => string | null;
  /** Why there is no `Run →`, shown once under the candidate list.
   *  Only read when `runHref` yields nothing — silence there would
   *  read as a missing feature rather than a missing site. */
  noRunNote?: string;
}

/** The Assessment heading and body, rendered identically whether the
 *  text is the streaming `partial_assessment` or the finished
 *  `result.assessment`.
 *
 *  It is one component for one reason: the run's answer must GROW in
 *  place. Two separate renderings -- however carefully matched today --
 *  drift, and the moment they do, the completion of every analysis
 *  becomes a visible jump exactly as the engineer starts reading. The
 *  two callers are mutually exclusive (`AnalysisJobView` shows the
 *  partial only while there is no `result.assessment`), so this
 *  heading appears at most once.
 *
 *  `label` is the single thing a caller may vary, and it exists for
 *  exactly one case: a partial that is all a FINISHED run ever
 *  produced. Keeping that text is right; presenting it under the same
 *  heading as a completed answer would be passing a truncated draft
 *  off as the model's conclusion. */
function AssessmentBlock({ text, label = 'Assessment' }: { text: string; label?: string }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-muted)', marginBottom: 2 }}>
        {label}
      </div>
      <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{text}</p>
    </div>
  );
}

/** What the run is doing right now, oldest first, each stamped with its
 *  offset from the start of the run.
 *
 *  This exists because the honest outcome of a young runbook catalogue
 *  is "matched no catalogued runbook" -- and waiting 60-90 seconds at a
 *  motionless "Status: running" for that answer reads as a hang, not as
 *  a result. The offsets are the second half of it: "47.3s · Searching
 *  logs" says the model is in a tool call, which is the difference
 *  between a slow run and a stuck one.
 *
 *  Deliberately quiet -- small, muted, monospaced offsets, no icons and
 *  no per-`kind` styling. `kind` is a growing server-side vocabulary
 *  (the api-server's own model says so), so nothing here branches on
 *  it: an entry whose kind this build has never heard of renders its
 *  `text` like any other, which is the only behaviour that keeps
 *  working when the backend adds a phase. It did on 2026-09-03
 *  (`reasoning`, "the model is thinking before it answers", for a
 *  reasoning model whose thinking used to be a silent 20-40 s gap in
 *  this list) and this component did not change for it.
 *
 *  Taken down once the run is `done`, and placed BELOW the assessment
 *  rather than above it, so that neither its growth nor its removal
 *  moves the answer the engineer is reading. */
function AnalysisProgressList({ job }: { job: SupportAnalysisResponse }) {
  const entries = job.progress ?? [];
  if (entries.length === 0) return null;
  return (
    <ol
      data-testid="analysis-progress"
      aria-label="Analysis progress"
      style={{
        listStyle: 'none',
        margin: '8px 0 0',
        padding: 0,
        fontSize: 12,
        color: 'var(--nx-muted)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {entries.map((entry, i) => (
        // Index keys: the list is strictly append-only server-side, so
        // entry `i` is always the same step.
        <li key={i} data-testid="analysis-progress-entry">
          <span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.75 }}>
            {formatOffset(entry.at_ms - job.created_at_ms)}
          </span>
          {' · '}
          {entry.text}
        </li>
      ))}
    </ol>
  );
}

/** The result block of a finished analysis. Deliberately names
 *  neither the model nor the tool-call count -- the engineer wants the
 *  diagnosis, not the plumbing (the derived signature is already
 *  visible in the Knowledge tab). The `assessment` comes first
 *  because it is the model's actual content and is worth reading even
 *  when no catalogued runbook matched -- on a young runbook catalogue
 *  that is the common outcome, and before this the whole run rendered
 *  as one "matched nothing" line.
 *
 *  The things it DOES say about the run itself are wall-clock time,
 *  whether the deadline cut it off and -- when it did -- how long the
 *  slowest model step took, because all three change how much to
 *  trust a thin answer and the last says which setting to change --
 *  that is diagnosis, not plumbing. The model's NAME is still not one
 *  of them: a product rule, pinned by test, not an oversight. */
export function AnalysisResultView({
  result,
  runHref,
  noRunNote,
}: { result: SupportAnalysisResult } & AnalysisRunTarget) {
  const noCandidates = result.candidates.length === 0;
  const hasAssessment = Boolean(result.assessment);
  const cutShort = analysisDeadlineExceeded(result);
  // The model's final text stays reachable whenever the pipeline
  // dropped or could not read it: any failure_reason (a non-tool-
  // calling model's prose; runbooks proposed outside the catalogue and
  // dropped -- exactly what the engineer wants to read), or a run that
  // parsed to nothing at all. Once the answer parsed into an
  // assessment or candidates, those ARE the answer and the raw text
  // would only duplicate them.
  const showRawAnswer =
    Boolean(result.raw_answer) &&
    (result.failure_reason != null || (noCandidates && !hasAssessment));
  // One note for the whole list rather than one per candidate: the
  // reason is a property of the target, not of any single runbook.
  const anyRunnable = result.candidates.some((c) => runHref?.(c.runbook_id));
  return (
    <div style={{ marginTop: 6 }}>
      {cutShort && (
        <p
          data-testid="analysis-deadline-note"
          style={{
            color: 'var(--nx-warning)',
            border: '1px solid var(--nx-warning)',
            borderRadius: 'var(--nx-r)',
            padding: '6px 10px',
            fontSize: 13,
            margin: '0 0 8px',
          }}
        >
          {deadlineNote(result)}
        </p>
      )}
      {result.assessment && <AssessmentBlock text={result.assessment} />}
      {noCandidates ? (
        <p style={{ color: 'var(--nx-muted)', margin: 0 }}>
          {emptyCandidatesCopy(result, hasAssessment)}
        </p>
      ) : (
        <>
          <ul>
            {result.candidates.map((c, i) => {
              const href = runHref?.(c.runbook_id) ?? null;
              return (
                <li key={i} style={{ marginBottom: 6 }}>
                  <span
                    style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}
                  >
                    <strong>{c.runbook_id}</strong>
                    <span>
                      ({c.confidence}
                      {c.prior_success_rate ? `, prior success ${c.prior_success_rate}` : ''}) —{' '}
                      {c.evidence}
                    </span>
                    {/* The candidate is only a suggestion until someone
                        runs it; this is the shortest path from reading the
                        name to standing in front of its confirm form. */}
                    {href && (
                      <RunLink
                        to={href}
                        testId={`run-candidate-${c.runbook_id}`}
                        title={`Open ${c.runbook_id} in Act, pre-filled for this issue`}
                      />
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
          {!anyRunnable && noRunNote && (
            <p
              data-testid="analysis-no-run-note"
              style={{ color: 'var(--nx-muted)', fontSize: 12, margin: '0 0 6px' }}
            >
              {noRunNote}
            </p>
          )}
        </>
      )}
      {showRawAnswer && (
        <details style={{ marginTop: 6 }}>
          <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--nx-muted)' }}>
            Model output (raw)
          </summary>
          <pre
            style={{
              marginTop: 6,
              maxHeight: 320,
              overflow: 'auto',
              background: 'var(--nx-bg3)',
              padding: 10,
              fontSize: 12,
              whiteSpace: 'pre-wrap',
            }}
          >
            {result.raw_answer}
          </pre>
        </details>
      )}
    </div>
  );
}

/** A whole analysis JOB: the status line (with wall-clock time once
 *  the server reports it), how old the answer is and the control that
 *  replaces it, a server-side error, the assessment -- streaming or
 *  final -- and the result block.
 *  Shared for the same reason as the result view -- the status line is
 *  the only thing telling an engineer a poll is still running, and two
 *  panes disagreeing about that would be worse than either wording.
 *
 *  Layout order is load-bearing, not aesthetic:
 *
 *    status/age/Re-analyze  (fixed height, never moves)
 *    assessment             (partial until a final one exists, final
 *                            once it does -- the SAME block, so the
 *                            text grows in place; a finished run that
 *                            produced no final assessment keeps its
 *                            partial, relabelled as incomplete)
 *    result detail          (candidates, raw output; done only)
 *    progress               (running only, BELOW everything, so taking
 *                            it down on completion moves nothing above it)
 */
export function AnalysisJobView({
  job,
  runHref,
  noRunNote,
  onReanalyze,
  reanalyzeDisabled,
  now = Date.now(),
}: {
  job: SupportAnalysisResponse;
  /** Re-run this target from scratch, skipping the server's cache.
   *  Rendered only when supplied -- a caller with no way to start a
   *  run should not show a button that cannot work. */
  onReanalyze?: () => void;
  /** Held down while a run is in flight. The caller owns this because
   *  it also knows about its own in-flight POST, which this job object
   *  cannot see (the new job does not exist yet). */
  reanalyzeDisabled?: boolean;
  /** Injectable clock for the age line. Defaults to now; a test pins
   *  it rather than pinning the whole environment's timers. */
  now?: number;
} & AnalysisRunTarget) {
  const elapsed = job.result?.elapsed_ms;
  const running = analysisIsRunning(job);
  // Never both: `partial_assessment` is a capped, possibly
  // mid-sentence preview, and the moment `result.assessment` exists it
  // is the authoritative text. Rendering the two together would
  // duplicate the Assessment heading, which is the visible symptom of
  // getting this wrong -- so the ONE condition is whether a final
  // assessment exists, and `AnalysisResultView` below renders its
  // block on exactly the same fact.
  //
  // What this must NOT do is key off `status`. It used to, and a run
  // that finished with no `result.assessment` at all -- a truncated or
  // unparseable answer, which is a routine outcome of the production
  // model pin -- deleted the text the engineer was watching being
  // written at the instant the run completed. There is no other copy
  // of it anywhere: the job's own partial is all that answer ever was.
  const finalAssessment = job.result?.assessment ?? null;
  const streaming = finalAssessment == null ? (job.partial_assessment ?? null) : null;
  // A partial on a job that is no longer going is not a preview of
  // anything -- it is the whole of what came back. Kept, but never
  // under the heading a completed answer wears.
  const streamingIsFinal = streaming != null && !running;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span>
          Status: {job.status}
          {elapsed != null ? ` · took ${formatElapsed(elapsed)}` : ''}
        </span>
        {/* The age and the button are ONE feature, not two: the server
            caches a finished analysis until somebody re-runs it, with
            no expiry at all, so a cached answer can outlive the
            situation it describes. Saying how old it is and offering
            to replace it are jointly what make that trade safe --
            either one alone is a trap. */}
        {job.status === 'done' && (
          <span
            data-testid="analysis-age"
            title={new Date(job.created_at_ms).toLocaleString()}
            style={{ color: 'var(--nx-muted)', fontSize: 12 }}
          >
            Analysed {formatAge(now - job.created_at_ms)}
            {job.cached ? ' · cached' : ''}
          </span>
        )}
        {/* Present for the whole life of a job, disabled while one is
            in flight, rather than appearing only once a run finishes: a
            control that comes and goes moves the two beside it every
            time, and "you may re-run this when it finishes" is a truer
            thing to show mid-run than nothing at all. */}
        {onReanalyze && (
          <button
            type="button"
            data-testid="analysis-reanalyze"
            onClick={onReanalyze}
            disabled={reanalyzeDisabled}
            title="Run a fresh analysis, ignoring the cached result"
            style={{ fontSize: 12 }}
          >
            Re-analyze
          </button>
        )}
      </div>
      {job.error && <div style={{ color: 'var(--nx-error)' }}>{job.error}</div>}
      {streaming && (
        // Same wrapper offset as `AnalysisResultView`'s, so the block
        // does not shift by 6px at the instant the run completes.
        <div style={{ marginTop: 6 }} data-testid="analysis-partial-assessment">
          <AssessmentBlock
            text={streaming}
            label={
              streamingIsFinal
                ? 'Assessment (incomplete — the model did not finish)'
                : 'Assessment'
            }
          />
        </div>
      )}
      {job.result && (
        <AnalysisResultView result={job.result} runHref={runHref} noRunNote={noRunNote} />
      )}
      {running && <AnalysisProgressList job={job} />}
    </div>
  );
}
